import { Component, inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';

import { ToastrService } from 'ngx-toastr';
import { ManufacturerService } from '../manufacturers.service';

@Component({
  selector: 'add-manufacturers',
  standalone: true,
  imports: [ReactiveFormsModule, RouterModule],
  templateUrl: './add-manufacturers.component.html',
  styleUrls: ['./add-manufacturers.component.scss'],
})
export class AddManufacturersComponent implements OnInit {
  manufacturerForm!: FormGroup;
  isEdit = false;
  manufacturerId: string | null = null;

  // Inject dependencies
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private toastr = inject(ToastrService);
  private manufacturerService = inject(ManufacturerService);

  ngOnInit(): void {
    this.initializeForm();

    this.manufacturerId = this.route.snapshot.paramMap.get('id');
    this.isEdit = !!this.manufacturerId;

    if (this.isEdit) {
      this.loadManufacturer();
    }
  }

  private initializeForm(): void {
    this.manufacturerForm = this.fb.group({
      name: ['', Validators.required],
      code: ['', Validators.required],
      brands: this.fb.array([this.fb.control('', Validators.required)]),
      contacts: this.fb.array([this.createContactFormGroup()]),
    });
  }

  private createContactFormGroup(): FormGroup {
    return this.fb.group({
      role: ['SALES_OFFICER', Validators.required],
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: ['', Validators.required],
    });
  }

  get brands(): FormArray {
    return this.manufacturerForm.get('brands') as FormArray;
  }

  get contacts(): FormArray {
    return this.manufacturerForm.get('contacts') as FormArray;
  }

  addBrand(): void {
    this.brands.push(this.fb.control('', Validators.required));
  }

  removeBrand(index: number): void {
    if (this.brands.length > 1) this.brands.removeAt(index);
  }

  addContact(): void {
    this.contacts.push(this.createContactFormGroup());
  }

  removeContact(index: number): void {
    if (this.contacts.length > 1) this.contacts.removeAt(index);
  }

  loadManufacturer(): void {
    this.manufacturerService
      .getManufacturerById(this.manufacturerId!)
      .subscribe({
        next: (data: any) => {
          this.manufacturerForm.patchValue({
            name: data.name,
            code: data.code,
          });

          // Set brands
          this.brands.clear();
          data.brands.forEach((brand: any) => {
            this.brands.push(this.fb.control(brand.name, Validators.required));
          });

          // Set contacts
          this.contacts.clear();
          data.contacts.forEach((contact: any) => {
            this.contacts.push(
              this.fb.group({
                role: [contact.role, Validators.required],
                name: [contact.contactPerson.name, Validators.required],
                email: [
                  contact.contactPerson.email,
                  [Validators.required, Validators.email],
                ],
                phoneNumber: [
                  contact.contactPerson.phoneNumber,
                  Validators.required,
                ],
              })
            );
          });
        },
        error: (err) => {
          console.error('Error loading manufacturer:', err);
          this.toastr.error('Failed to load manufacturer data');
        },
      });
  }

  goBack(): void {
    this.router.navigate(['/vendors']);
  }

  submit(): void {
    if (this.manufacturerForm.invalid) {
      this.manufacturerForm.markAllAsTouched();
      this.toastr.error('Please fill in all required fields correctly');
      return;
    }

    const payload = this.manufacturerForm.value;

    if (this.isEdit) {
      this.manufacturerService
        .updateManufacturer(this.manufacturerId!, payload)
        .subscribe({
          next: () => {
            this.toastr.success('Manufacturer updated successfully');
            this.goBack();
          },
          error: () => {
            this.toastr.error('Failed to update manufacturer');
          },
        });
    } else {
      this.manufacturerService.addManufacturer(payload).subscribe({
        next: () => {
          this.toastr.success('Manufacturer created successfully');
          this.goBack();
        },
        error: () => {
          this.toastr.error('Failed to create manufacturer');
        },
      });
    }
  }
}
